import { Editor } from "@tiptap/core";
import { Node as PMNode } from "@tiptap/pm/model";
import { NodeView } from "prosemirror-view";

import { setNodeViewAttrs } from "@/editor/adapters/utils";

import { getEditorInstanceId } from "../editor";
import { NWNodeView } from "./nodeView";

type RecordValue = string | number | boolean | undefined | null;

/**
 * Type helper to extract function signatures from an actions record
 */
type ActionFunction = (...args: any[]) => void;

/**
 * Type helper for wrapped action handlers
 */
type WrappedActionHandler<E, P, F extends ActionFunction> = (context: {
  component: E;
  updateAttrs: (attrs: Partial<P>) => void;
  params: Parameters<F>;
}) => void;

/**
 * Options for creating a web component node view
 */
export interface NodeViewOptions<
  Element extends HTMLElement,
  Props extends Record<string, RecordValue>,
  Actions extends Record<string, ActionFunction>,
> {
  /**
   * The tag name of the web component
   */
  tagName: string;

  /**
   * The editor instance
   */
  editor: Editor;

  /**
   * The node being rendered
   */
  node: PMNode;

  /**
   * Function to get the current position of the node
   */
  getPos: () => number;

  /**
   * Whether the node view should handle its own content
   */
  contentEditable?: boolean;

  /**
   * Initial props to set on the web component
   */
  initialProps?: Partial<Props>;

  /**
   * Handlers to be exposed to the web component
   */
  actions?: {
    [K in keyof Actions]: WrappedActionHandler<Element, Props, Actions[K]>;
  };

  /**
   * Whether to isolate the node view from the editor's event handling
   */
  isolate?: boolean;
}

/**
 * Creates a node view for a web component with typed props and direct handler access
 */
export function createNodeView<
  Element extends NWNodeView<Props, Actions>,
  Props extends Record<string, RecordValue>,
  Actions extends Record<string, ActionFunction>,
>({
  tagName,
  editor,
  node,
  getPos,
  actions,
  contentEditable = false,
  initialProps = {} as Partial<Props>,
  isolate = true,
}: NodeViewOptions<Element, Props, Actions>): NodeView {
  const element = document.createElement(tagName) as NWNodeView;

  // Helper to update node attributes
  const updateAttrs = (newAttrs: Partial<Props>) => {
    editor.commands.command(({ tr }) => {
      const pos = getPos();

      // Might be a better alternative to setNodeMarkup
      // Object.entries(newAttrs).forEach(([key, value]) => {
      //    tr.setNodeAttribute(pos, key, value);
      // });

      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        ...newAttrs,
      });
      return true;
    });
  };

  // Create wrapped actions with proper typing
  const wrappedActions = {} as Actions;

  if (actions) {
    for (const [name, action] of Object.entries(actions)) {
      wrappedActions[name as keyof Actions] = ((...args: Parameters<Actions[keyof Actions]>) =>
        action({
          component: element,
          updateAttrs,
          params: args,
        })) as Actions[keyof Actions];
    }
  }

  element.viewActions = wrappedActions;
  element.editorInstanceId = getEditorInstanceId(editor);

  setNodeViewAttrs(element, { ...node.attrs, ...initialProps });

  const nodeView: NodeView = {
    dom: element,
    contentDOM: contentEditable ? element : undefined,
    update: updatedNode => {
      if (updatedNode.type !== node.type) return false;
      setNodeViewAttrs(element, { ...updatedNode.attrs, ...initialProps });
      return true;
    },
    destroy: () => {
      element.viewActions = {} as Actions;
    },
  };

  // Optionally isolate the node view
  if (isolate) {
    return {
      ...nodeView,
      stopEvent: () => true,
      ignoreMutation: () => true,
    };
  }

  return nodeView;
}
